// Reine Logik des Kundentagebuchs — ohne React/DOM, damit vitest-testbar
// (Muster: contactVorgaengeHelpers.ts, createFlowLogic.ts, mentions.ts).

import type { ContactNoteDto, Gespraechspartner } from "../../services/contactNoteService";

/** Eine Notiz mit ihren Kommentaren — das Anzeigemodell des Tagebuchs. */
export interface NotizMitKommentaren {
    note: ContactNoteDto;
    kommentare: ContactNoteDto[];
}

/**
 * Gruppiert die flache Serverliste in Top-Level-Notizen + ihre Kommentare.
 *
 * Die Liste kommt neueste-zuerst; Notizen behalten diese Reihenfolge,
 * Kommentare werden innerhalb einer Notiz ÄLTESTE-zuerst sortiert (Gesprächs-
 * verlauf liest sich von oben nach unten). Kommentare ohne existierende
 * Eltern-Notiz (z. B. Eltern gelöscht) fallen bewusst weg, statt verwaist
 * aufzutauchen.
 */
export function groupNotes(notes: ContactNoteDto[]): NotizMitKommentaren[] {
    const tops = notes.filter((n) => n.parentId == null);
    const byParent = new Map<number, ContactNoteDto[]>();
    for (const n of notes) {
        if (n.parentId == null) continue;
        const list = byParent.get(n.parentId);
        if (list) list.push(n);
        else byParent.set(n.parentId, [n]);
    }
    return tops.map((note) => ({
        note,
        kommentare: (byParent.get(note.id) ?? []).slice().sort(sortByCreatedAtAsc),
    }));
}

function sortByCreatedAtAsc(a: ContactNoteDto, b: ContactNoteDto): number {
    const ta = Date.parse(a.createdAt);
    const tb = Date.parse(b.createdAt);
    if (Number.isNaN(ta) || Number.isNaN(tb)) return a.id - b.id;
    return ta - tb || a.id - b.id;
}

/** Datum + Uhrzeit einer Notiz, z. B. „18.07.2026, 14:22". */
export function formatWhen(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

/**
 * Zerlegt einen Notiztext in Segmente, damit @-Erwähnungen hervorgehoben
 * werden können. Erkennt dieselbe Token-Form wie `resolveTypedMentions`
 * (`/@([\w.-]+)/`), arbeitet aber rein textuell — die Auflösung auf echte
 * User passiert in der Komponente.
 */
export function splitMentions(
    text: string,
): Array<{ kind: "text" | "mention"; value: string }> {
    const out: Array<{ kind: "text" | "mention"; value: string }> = [];
    const re = /@([\w.-]+)/g;
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
        if (m.index > last) out.push({ kind: "text", value: text.slice(last, m.index) });
        out.push({ kind: "mention", value: m[0] });
        last = m.index + m[0].length;
    }
    if (last < text.length) out.push({ kind: "text", value: text.slice(last) });
    return out;
}

/**
 * Prefill für „aus dieser Notiz einen Vorgang machen": die Zuweisung der Notiz
 * wird übernommen, damit im Erstell-Flow nur noch Betreff und Bearbeiter zu
 * wählen sind.
 *
 * Der Betreff bleibt bewusst LEER — der Notiztext ist ein Gesprächsprotokoll
 * und ergäbe keinen brauchbaren Vorgangstitel (gleiche Entscheidung wie in
 * `mailVorgangPrefill.ts`, wo der Mailbetreff bewusst nicht übernommen wird).
 *
 * `labels` liefert die Anzeigetexte, die der Erstell-Flow für seine Slots
 * erwartet (`contractNbr` bzw. `name`); fehlt ein Label, wird ein sprechender
 * Platzhalter gesetzt, damit der Slot nicht leer wirkt.
 */
export function buildVorgangPrefillFromNote(
    /**
     * Nur die drei Felder, die den Prefill ausmachen — nicht die volle DTO: die
     * Blockfläche kennt eine Notiz als Eintrag des zusammengeführten Stroms
     * (`KundenNotizDto`), nicht als `ContactNoteDto`. Beide erfüllen diese
     * Form; ein Cast wäre nur eine ausgeschaltete Prüfung.
     */
    note: Pick<ContactNoteDto, "contactId" | "contractId" | "riskObjectId">,
    labels?: { vertragNbr?: string | null; risikoName?: string | null },
): {
    initialContactId: number;
    initialVertrag?: { id: number; contractNbr: string; contactId?: number | null };
    initialRisiko?: { id: number; name: string; contactId?: number | null };
} {
    return {
        initialContactId: note.contactId,
        ...(note.contractId != null
            ? {
                  initialVertrag: {
                      id: note.contractId,
                      contractNbr: labels?.vertragNbr || `Vertrag ${note.contractId}`,
                      contactId: note.contactId,
                  },
              }
            : {}),
        ...(note.riskObjectId != null
            ? {
                  initialRisiko: {
                      id: note.riskObjectId,
                      name: labels?.risikoName || `Risiko ${note.riskObjectId}`,
                      contactId: note.contactId,
                  },
              }
            : {}),
    };
}

/** Trennt Gesprächspartner in interne userIds und externe contactIds. */
export function splitGespraechspartner(list: Gespraechspartner[] | undefined): {
    intern: string[];
    extern: number[];
} {
    const intern: string[] = [];
    const extern: number[] = [];
    for (const gp of list ?? []) {
        if (gp.kind === "intern") intern.push(gp.userId);
        else if (gp.kind === "extern") extern.push(gp.contactId);
    }
    return { intern, extern };
}

/** Baut aus getrennten Listen wieder das gemischte Wire-Format. */
export function joinGespraechspartner(
    intern: string[],
    extern: number[],
): Gespraechspartner[] {
    return [
        ...intern.map((userId) => ({ kind: "intern" as const, userId })),
        ...extern.map((contactId) => ({ kind: "extern" as const, contactId })),
    ];
}
